// Ultimate Card Game (UCG) - Campaign Cutscene Timelines
//
// Data for js/engine/cutscenePlayer.js. Coordinates are world-space
// (Battlefield is a fixed 1600x540 world, laneY=176, laneHeight=320 - see
// js/engine/battlefield.js). Content follows the story notes in
// docs/lose story gedanken.rtf and the choreography written up in
// docs/game-design-singleplayer-kampagne.md Kapitel 9.2. The heli and the
// Shade are real `unitActor` units (docs/singleplayer implementation
// soft-foraging-flamingo.md Phase 3/4) - fully sprited, not placeholder
// markers; the animal stays a plain `actor` (see cutscenePlayer.js's
// renderActors() circle fallback) since it never needs real game sprites.
//
// `zoom` on a camera/cameraFollow step is a MULTIPLIER on
// Viewport.baseZoom (the "whole map fits on screen" fit-to-screen level),
// exactly like Game._startCameraIntro()'s __introZoomMult (js/main.js) -
// not an absolute zoom value. At the multiplier 1.0 the visible area
// already covers the full 1600x540 world, which leaves zero room to pan
// toward an x/y target (Viewport.updateUnitTracking() clamps everything
// back to center in that case) - so every step here uses 2.0+ to actually
// carve out a sub-region of the map to frame ("man darf nicht so weit
// blicken dürfen" - docs/game-design-singleplayer-kampagne.md).

export const CAMPAIGN_CUTSCENES = {
  // "Straßenstück im Wald" -> "Plasma-Schrottfeld": Helikopter-Anflug,
  // Fallschirmabsprung, kurzes Tier-Erschrecken, dann ein durchgehender
  // Fußmarsch zu den Marine-Wachen - die Kamera folgt dem Shade eng die
  // ganze Strecke, statt per Schnitt vorzugreifen, sodass die Gegner erst
  // sichtbar werden, wenn der Marsch dort tatsächlich ankommt.
  m1_forest_road: {
    steps: [
      { type: 'hud', visible: false, at: 0 },
      { type: 'unitActor', actorId: 'heli', cardName: 'A9 Piranha', team: 'western', x: 60, y: 150, at: 0 },
      { type: 'actor', actorId: 'heli', anim: 'flying', path: [{ x: 60, y: 150 }, { x: 250, y: 150 }], duration: 1.4, at: 0 },
      // Kamera folgt dem Heli von der allerersten Frame an eng - kein
      // separater weiter Etablierungs-Schnitt mehr ("die Kamera muss über
      // die komplette Dauer VIEL näher ans Geschehen heran").
      { type: 'cameraFollow', actorId: 'heli', zoom: 4.5, duration: 1.4, at: 0 },
      { type: 'actorHide', actorId: 'heli', at: 1.6 },
      // Mission-1-Encounter-Override (docs/singleplayer implementation
      // soft-foraging-flamingo.md), analog zur Black-Marine-hpOverride:
      // doppelte HP (40->80), doppelte Feuerrate (attackSpeed ist die
      // Sekundenzahl zwischen Schüssen - js/engine/unit.js "seconds between
      // attacks" -, also halbiert statt verdoppelt: 3.0->1.5) und
      // isCloaked:false - "der Shade nutzt in diesem frühen Story-Abschnitt
      // nicht seine Tarnfähigkeit" (docs/game-design-singleplayer-
      // kampagne.md Kapitel 9.1) war schon immer die Absicht, wurde aber
      // nie tatsächlich unterdrückt; ohne diesen Override rendert die
      // reguläre Shade-Karte mit isCloaked:true dauerhaft halbtransparent.
      // Die reale PvP-"Shade"-Karte bleibt unangetastet - das ist ein Klon,
      // nie eine Mutation von CARDS_DATA.
      { type: 'unitActor', actorId: 'shade', cardName: 'Shade', team: 'western', x: 255, y: -30, isParachuting: true, parachuteDuration: 1.8, cardOverrides: { hp: 80, attackSpeed: 1.5, isCloaked: false }, at: 1.0 },
      { type: 'actor', actorId: 'shade', anim: 'parachute', path: [{ x: 255, y: -30 }, { x: 255, y: 300 }], duration: 1.8, at: 1.0 },
      { type: 'camera', to: { x: 255, y: 300, zoom: 6.5 }, duration: 1.8, at: 1.0 },
      { type: 'actor', actorId: 'animal', anim: 'flee', path: [{ x: 400, y: 300 }, { x: 620, y: 300 }], duration: 0.8, at: 3.0 },
      { type: 'actorHide', actorId: 'animal', at: 4.0 },
      // "Umschauen": ~1s Stand-Pause nach der Landung, bevor der Fußmarsch
      // beginnt - reine Timing-Verschiebung (isCinematic hält den Shade
      // zwischen Schritten ohnehin bewegungs-/kampflos; die Beine springen
      // dabei nicht mehr auf der Stelle - js/engine/render/units.js
      // vergleicht die Position zum Vorframe und schaltet auf die
      // Steh-Pose, siehe docs/singleplayer implementation soft-foraging-
      // flamingo.md).
      //
      // Fußmarsch, der KURZ VOR dem liegenden Preserver Lock endet (der Prop
      // selbst sitzt bei x:630 - js/data/campaignMissions.js's groundProps -
      // 40px weiter, damit der Shade davor stehen bleibt statt mittendrin),
      // NICHT bis zu den Marines - "geht er langsam": deutlich geringere
      // Durchschnittsgeschwindigkeit als der frühere Marsch bis zu den
      // Gegnern (590-255=335px auf 6.5s statt 645px auf 5.5s).
      { type: 'actor', actorId: 'shade', anim: 'walk', path: [{ x: 255, y: 300 }, { x: 400, y: 306 }, { x: 480, y: 310 }, { x: 590, y: 315 }], duration: 6.5, at: 5.0 },
      { type: 'cameraFollow', actorId: 'shade', zoom: 5.5, duration: 6.5, at: 5.0 },
      // Ankunft vor dem Preserver Lock: Shade bleibt kurz stehen (keine
      // weiteren Actor-Schritte bis 12.7 - die Steh-Pose greift automatisch).
      //
      // "Dann bemerkt er die Gegner, noch recht weit weg": bleibt auf
      // demselben engen Zoom wie der Marsch-Follow (kein Rauszoomen, das die
      // Immersion brechen würde) - die Distanz wird stattdessen über eine
      // Kamera-SCHWENKBEWEGUNG erzählt, die zu den Marine-Wachen (950/300,
      // 1000/360) hinüberschwenkt (Zielpunkt 900/325 statt derselben x wie
      // der Shade, damit beide bei diesem engen Zoom tatsächlich ins Bild
      // passen) und wieder zurück, statt beide gemeinsam in einem weiten
      // Standbild zu zeigen.
      { type: 'camera', to: { x: 900, y: 325, zoom: 5.5 }, duration: 0.9, at: 12.7 },
      // Hält die Einstellung 1s lang bei den Gegnern, bevor der Rückschwenk
      // beginnt (at: 13.6 Ankunft + 1.0s Halt = 14.6), statt sofort
      // zurückzuschwenken.
      { type: 'camera', to: { x: 590, y: 316, zoom: 5.5 }, duration: 0.9, at: 14.6 },
      // Finale Einstellung, noch näher als der Rest der Szene - in diesem
      // Bild endet die Cutscene (kein `hud`-Schritt mehr: Game.
      // startCampaignMission()s onComplete-Callback blendet stattdessen die
      // reduzierte Kampagnen-HUD mit der einen spielbaren Karte ein, siehe
      // docs/singleplayer implementation soft-foraging-flamingo.md).
      { type: 'camera', to: { x: 590, y: 314, zoom: 7.5 }, duration: 1.0, at: 15.9 }
      // Die Shade-Einheit bleibt isCinematic:true und wird NICHT entfernt -
      // sie wartet auf die Aktivierung durch das Kartenspiel
      // (js/commands/commandProcessor.js's _play(), siehe docs/singleplayer
      // implementation soft-foraging-flamingo.md Phase 5).
    ]
  }

  // m1_victory_continue wurde entfernt: die Sieges-Cutscene wird jetzt
  // dynamisch aus der tatsächlichen Shade-Position gebaut (Game.
  // _buildCampaignVictoryTimeline() in js/main.js), da die feste Zielposition
  // hier nie zur echten Endposition des Kampfs gepasst hätte.
};
